import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../services/api';
import { canonicalizeNavigationPath, navigationPathsEqual } from '../lib/navigationPaths';
import type { NavigationPinnedView, NavigationProfile } from '../types/module';

const EMPTY_PROFILE: NavigationProfile = {
    pinned_paths: [],
    pinned_views: [],
    collapsed_groups: [],
    recent_paths: [],
    home_role_layout: [],
};

export function useNavigationProfile(enabled: boolean) {
    const [profile, setProfile] = useState<NavigationProfile>(EMPTY_PROFILE);
    const [loading, setLoading] = useState(false);

    const refresh = useCallback(async () => {
        if (!enabled) {
            setProfile(EMPTY_PROFILE);
            return;
        }
        setLoading(true);
        try {
            const loaded = await api.system.getNavigationProfile();
            setProfile({
                pinned_paths: loaded.pinned_paths || [],
                pinned_views: loaded.pinned_views || [],
                collapsed_groups: loaded.collapsed_groups || [],
                recent_paths: loaded.recent_paths || [],
                home_role_layout: loaded.home_role_layout || [],
            });
        } catch (error) {
            setProfile(EMPTY_PROFILE);
        } finally {
            setLoading(false);
        }
    }, [enabled]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const persistProfile = useCallback(async (nextProfile: NavigationProfile) => {
        if (!enabled) return;
        setProfile(nextProfile);
        try {
            const saved = await api.system.updateNavigationProfile(nextProfile);
            setProfile(saved);
        } catch (error) {
            // Keep optimistic local state to avoid disruptive UX.
        }
    }, [enabled]);

    const togglePinnedPath = useCallback(async (path: string) => {
        if (!enabled || !path) return;
        const nextPath = canonicalizeNavigationPath(path);
        const existing = [...(profile.pinned_paths || [])];
        const set = new Set(existing.filter((entry) => !navigationPathsEqual(entry, nextPath)).map((entry) => canonicalizeNavigationPath(entry)));
        const alreadyPinned = existing.some((entry) => navigationPathsEqual(entry, nextPath));
        if (!alreadyPinned) set.add(nextPath);
        await persistProfile({
            ...profile,
            pinned_paths: Array.from(set),
        });
    }, [enabled, persistProfile, profile]);

    const togglePinnedView = useCallback(async (view: NavigationPinnedView) => {
        if (!enabled || !view?.id) return;
        const existing = profile.pinned_views || [];
        const nextViews = existing.some((entry) => entry.id === view.id)
            ? existing.filter((entry) => entry.id !== view.id)
            : [...existing, view];
        await persistProfile({
            ...profile,
            pinned_views: nextViews,
        });
    }, [enabled, persistProfile, profile]);

    const removePinnedView = useCallback(async (id: string) => {
        if (!enabled || !id) return;
        await persistProfile({
            ...profile,
            pinned_views: (profile.pinned_views || []).filter((entry) => entry.id !== id),
        });
    }, [enabled, persistProfile, profile]);

    const toggleCollapsedGroup = useCallback(async (group: string) => {
        if (!enabled || !group) return;
        const set = new Set(profile.collapsed_groups || []);
        if (set.has(group)) set.delete(group);
        else set.add(group);
        await persistProfile({
            ...profile,
            collapsed_groups: Array.from(set),
        });
    }, [enabled, persistProfile, profile]);

    const trackNavigation = useCallback(async (path: string, label?: string, source = 'sidebar') => {
        if (!enabled || !path) return;
        try {
            await api.system.trackNavigationEvent({ path, label, source });
        } catch (error) {
            // Non-blocking telemetry.
        }
    }, [enabled]);

    const pinnedPaths = useMemo(() => new Set(profile.pinned_paths || []), [profile.pinned_paths]);
    const pinnedViews = useMemo(() => profile.pinned_views || [], [profile.pinned_views]);
    const collapsedGroups = useMemo(() => new Set(profile.collapsed_groups || []), [profile.collapsed_groups]);

    return {
        profile,
        loading,
        refresh,
        pinnedPaths,
        pinnedViews,
        collapsedGroups,
        togglePinnedPath,
        togglePinnedView,
        removePinnedView,
        toggleCollapsedGroup,
        trackNavigation,
    };
}
